import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { User } from '../types';
import { getCurrentUser, completeReferralSignup } from '../api/api';
import { getHankoClient, getHankoApiUrl, onHankoClientReady, HankoClient } from '../lib/hanko';
import { useSnackbar } from './snackbarContext';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    isAuthenticated: boolean;
    isOptimistic: boolean;
    isDeletingAccount: boolean;
    setIsDeletingAccount: (isDeleting: boolean) => void;
    clearAccountSession: () => Promise<void>;
    login: (token?: string, refreshToken?: string) => Promise<void> | void;
    refreshUser: (expectedUserId?: string) => Promise<boolean>;
    logout: () => Promise<void> | void;
    updatePreferredPrompt: (promptId: string) => void;
}

export const AuthContext = createContext<AuthContextType>({
    user: null,
    loading: true,
    isAuthenticated: false,
    isOptimistic: false,
    isDeletingAccount: false,
    setIsDeletingAccount: () => { },
    clearAccountSession: async () => { },
    login: () => { },
    refreshUser: async () => false,
    logout: () => { },
    updatePreferredPrompt: () => { },
});

export const useAuth = () => useContext(AuthContext);

interface ApiErrorDetails {
    code?: string;
    status?: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
    typeof value === 'object' && value !== null
);

const getApiErrorDetails = (error: unknown): ApiErrorDetails => {
    if (!isRecord(error) || !isRecord(error.response)) return {};
    const response = error.response;
    const data = isRecord(response.data) ? response.data : null;
    const apiError = data && isRecord(data.error) ? data.error : null;

    return {
        ...(typeof response.status === 'number' ? { status: response.status } : {}),
        ...(apiError && typeof apiError.code === 'string' ? { code: apiError.code } : {}),
    };
};

export const cleanupLegacyDeletionStorage = (): void => {
    try {
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem('minifil.isDeletingAccount');
            sessionStorage.removeItem('minifil.deletionStatusToken');
            const keysToRemove: string[] = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && (key === 'minifil.deletionStatusToken' || key.startsWith('minifil.deletionStatusToken.'))) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach((k) => sessionStorage.removeItem(k));
        }
    } catch {
        // Storage exceptions caught
    }

    try {
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('minifil.isDeletingAccount');
            localStorage.removeItem('minifil.deletionStatusToken');
            const keysToRemove: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && (key === 'minifil.deletionStatusToken' || key.startsWith('minifil.deletionStatusToken.'))) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach((k) => localStorage.removeItem(k));
        }
    } catch {
        // Storage exceptions caught
    }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const [isDeletingAccount, setIsDeletingAccount] = useState<boolean>(false);
    const isDeletingAccountRef = useRef(isDeletingAccount);
    isDeletingAccountRef.current = isDeletingAccount;

    const referralCompletedRef = useRef(false);
    const currentUserRef = useRef<User | null>(null);
    const loadRequestGenerationRef = useRef(0);
    const sessionGenerationRef = useRef(0);

    const notifyServiceWorkerSessionChanged = useCallback((userId: string | null) => {
        if (typeof navigator !== 'undefined' && navigator.serviceWorker?.controller) {
            navigator.serviceWorker.controller.postMessage({
                type: 'AUTH_SESSION_CHANGED',
                userId,
            });
        }
    }, []);

    const clearAccountSession = useCallback(async (): Promise<void> => {
        loadRequestGenerationRef.current += 1;
        sessionGenerationRef.current += 1;
        setLoading(false);
        await queryClient.cancelQueries();
        currentUserRef.current = null;
        setUser(null);
        queryClient.clear();
        notifyServiceWorkerSessionChanged(null);
    }, [queryClient, notifyServiceWorkerSessionChanged]);

    const handleSessionClear = useCallback(() => {
        loadRequestGenerationRef.current += 1;
        sessionGenerationRef.current += 1;
        setLoading(false);
        currentUserRef.current = null;
        setUser(null);
        queryClient.clear();
        notifyServiceWorkerSessionChanged(null);
    }, [queryClient, notifyServiceWorkerSessionChanged]);

    // Referral completion utility
    const completeReferralIfPresent = useCallback(async (userId: string) => {
        if (referralCompletedRef.current) {
            return;
        }

        const referralCode = sessionStorage.getItem('referralCode');
        const referralInfo = sessionStorage.getItem('referralInfo');

        if (!referralCode || !referralInfo) {
            return;
        }

        try {
            referralCompletedRef.current = true;

            // Check if self-referral
            if (referralInfo) {
                try {
                    const parsedInfo = JSON.parse(referralInfo);
                    if (parsedInfo.inviterUserId === userId) {
                        sessionStorage.removeItem('referralCode');
                        sessionStorage.removeItem('referralInfo');
                        return;
                    }
                } catch (parseError) {
                    console.warn('Could not parse referral info', parseError);
                }
            }

            await completeReferralSignup(referralCode, userId);

            queryClient.invalidateQueries({ queryKey: ['user-limits', userId] });
            queryClient.invalidateQueries({ queryKey: ['subscription'] });

            sessionStorage.removeItem('referralCode');
            sessionStorage.removeItem('referralInfo');
        } catch (error: unknown) {
            console.error('Failed to complete referral signup', error);
            const { code, status } = getApiErrorDetails(error);

            if (code === 'REFERRAL_ALREADY_COMPLETED') {
                sessionStorage.removeItem('referralCode');
                sessionStorage.removeItem('referralInfo');
                showSnackbar(t('account.referral.already_completed'), 'warning');
            } else if (code === 'REFERRAL_NOT_FOUND') {
                sessionStorage.removeItem('referralCode');
                sessionStorage.removeItem('referralInfo');
                showSnackbar('Invalid referral code. Please check the link and try again.', 'error');
            } else if (code === 'SELF_REFERRAL_NOT_ALLOWED') {
                sessionStorage.removeItem('referralCode');
                sessionStorage.removeItem('referralInfo');
                showSnackbar('You cannot refer yourself. Please use a different referral link.', 'warning');
            } else if (status === 429) {
                showSnackbar('Too many referral attempts. Please try again later.', 'warning');
            } else if (code === 'INVITER_BANNED') {
                sessionStorage.removeItem('referralCode');
                sessionStorage.removeItem('referralInfo');
                showSnackbar('This referral link is no longer valid. Please use a different one.', 'error');
            } else if (code === 'REFERRAL_OWNERSHIP_MISMATCH') {
                sessionStorage.removeItem('referralCode');
                sessionStorage.removeItem('referralInfo');
                showSnackbar('Invalid referral link. Please use a different one.', 'error');
            } else if (code === 'UNAUTHORIZED_REFERRAL_COMPLETION') {
                sessionStorage.removeItem('referralCode');
                sessionStorage.removeItem('referralInfo');
                showSnackbar('You are not authorized to complete this referral.', 'error');
            } else if (code === 'EXISTING_USER_REFERRAL_NOT_ALLOWED') {
                sessionStorage.removeItem('referralCode');
                sessionStorage.removeItem('referralInfo');
                showSnackbar(t('account.referral.existing_user_not_allowed'), 'warning');
            } else {
                showSnackbar('Failed to complete referral. Please try again later.', 'error');
            }
        }
    }, [showSnackbar, t, queryClient]);

    const loadUser = useCallback(async (expectedUserId?: string): Promise<boolean> => {
        const requestGeneration = loadRequestGenerationRef.current + 1;
        loadRequestGenerationRef.current = requestGeneration;
        const sessionGeneration = sessionGenerationRef.current;
        const expectedLocalUserId = expectedUserId;
        let shouldClearLoading = true;

        if (isDeletingAccountRef.current) {
            setLoading(false);
            return false;
        }

        try {
            const userData = await getCurrentUser();

            // Guard against accepting a late response while deletion result state is active
            if (
                isDeletingAccountRef.current
                || sessionGenerationRef.current !== sessionGeneration
                || loadRequestGenerationRef.current !== requestGeneration
            ) {
                shouldClearLoading = false;
                return false;
            }

            if (
                expectedLocalUserId
                && (currentUserRef.current?.id !== expectedLocalUserId || userData?.id !== expectedLocalUserId)
            ) {
                shouldClearLoading = false;
                return false;
            }

            // Compare old and new local user IDs before accepting a new identity
            if (currentUserRef.current && currentUserRef.current.id !== userData.id) {
                notifyServiceWorkerSessionChanged(null);
            }

            currentUserRef.current = userData;
            setUser(userData);
            queryClient.setQueryData<User | null>(['user'], userData);

            if (userData?.id) {
                completeReferralIfPresent(userData.id).catch((err) => {
                    console.warn('Referral completion failed after user load:', err);
                });
            }
            return true;
        } catch (error: unknown) {
            if (
                isDeletingAccountRef.current
                || sessionGenerationRef.current !== sessionGeneration
                || loadRequestGenerationRef.current !== requestGeneration
            ) {
                shouldClearLoading = false;
                return false;
            }
            if (getApiErrorDetails(error).status === 401) {
                handleSessionClear();
            } else {
                console.error('Error loading user:', error);
            }
            return false;
        } finally {
            if (shouldClearLoading) {
                setLoading(false);
            }
        }
    }, [completeReferralIfPresent, handleSessionClear, notifyServiceWorkerSessionChanged, queryClient]);

    const loadUserRef = useRef(loadUser);
    loadUserRef.current = loadUser;

    const handleSessionClearRef = useRef(handleSessionClear);
    handleSessionClearRef.current = handleSessionClear;

    useEffect(() => {
        cleanupLegacyDeletionStorage();

        const apiUrl = getHankoApiUrl();
        const unsubs: Array<() => void> = [];
        let subscribedClient: HankoClient | null = null;
        let isDisposed = false;

        const cleanupHankoListeners = (): void => {
            unsubs.splice(0).forEach((unsub) => unsub());
            subscribedClient = null;
        };

        const setupHankoListeners = (hankoClient: HankoClient): void => {
            if (isDisposed || subscribedClient === hankoClient) {
                return;
            }

            cleanupHankoListeners();
            subscribedClient = hankoClient;
            unsubs.push(
                hankoClient.on('sessionCreated', () => {
                    void loadUserRef.current();
                })
            );

            unsubs.push(
                hankoClient.on('sessionExpired', () => {
                    handleSessionClearRef.current();
                })
            );

            unsubs.push(
                hankoClient.on('userLoggedOut', () => {
                    handleSessionClearRef.current();
                })
            );

            unsubs.push(
                hankoClient.on('accountDeleted', () => {
                    handleSessionClearRef.current();
                })
            );
        };

        const removeReadyListener = onHankoClientReady(setupHankoListeners);

        const initializeHanko = async (): Promise<void> => {
            // Initial user load should start without waiting for Hanko element registration.
            void loadUserRef.current();

            try {
                const hankoClient = await getHankoClient(apiUrl);
                setupHankoListeners(hankoClient);
            } catch (error) {
                console.error('Failed to initialize Hanko client:', error);
            }
        };

        void initializeHanko();

        return () => {
            isDisposed = true;
            removeReadyListener();
            cleanupHankoListeners();
        };
    }, []);

    const login = useCallback(async () => {
        await loadUser();
    }, [loadUser]);

    const refreshUser = useCallback(async (expectedUserId?: string): Promise<boolean> => {
        return loadUser(expectedUserId ?? currentUserRef.current?.id);
    }, [loadUser]);

    const logout = useCallback(async () => {
        try {
            const hankoClient = await getHankoClient(getHankoApiUrl());
            await hankoClient.logout();
        } catch (err) {
            console.error('Error during Hanko logout:', err);
        } finally {
            await clearAccountSession();
        }
    }, [clearAccountSession]);

    const updatePreferredPrompt = useCallback((promptId: string) => {
        setUser((prev) => {
            if (!prev) return prev;
            const nextUser = { ...prev, preferredPromptId: promptId };
            queryClient.setQueryData<User | null>(['user'], nextUser);
            return nextUser;
        });
    }, [queryClient]);

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                isAuthenticated: !!user,
                isOptimistic: false,
                isDeletingAccount,
                setIsDeletingAccount,
                clearAccountSession,
                login,
                refreshUser,
                logout,
                updatePreferredPrompt,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
